import React from "react";
import { Box, Grid, Stack, useTheme, Card, Typography, Chip, Avatar } from "@mui/material";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import RecommendIcon from "@mui/icons-material/Recommend";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { CircularProgressIndicator } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { predictiveOverallScore, predictiveHighlights, predictiveRecommendations, predictiveAreasToWatch } from "../data";
import SpaIcon from "@mui/icons-material/Spa";

export const PredictiveInsights: React.FC = () => {
  const theme = useTheme();

  const impactColors = { positive: theme.palette.success.main, neutral: theme.palette.info.main, negative: theme.palette.error.main };
  const priorityColors = { high: theme.palette.error.main, medium: theme.palette.warning.main, low: theme.palette.success.main };
  const severityColors = { red: theme.palette.error.main, amber: theme.palette.warning.main, green: theme.palette.success.main };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Overall Wellness Forecast" subtitle="AI-predicted wellness trajectory.">
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgressIndicator
                  value={predictiveOverallScore}
                  label="Wellness Score"
                  icon={<SpaIcon fontSize="small" />}
                  color={theme.palette.success.main}
                  size={140}
                />
              </Box>
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard title="Key Highlights" subtitle="AI-identified trends and patterns.">
              <Stack spacing={2}>
                {predictiveHighlights.map((item, index) => (
                  <Card key={index} variant="outlined" sx={{ p: 2, borderLeft: `4px solid ${impactColors[item.impact]}` }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                      <Avatar sx={{ bgcolor: `${impactColors[item.impact]}20`, width: 40, height: 40 }}>
                        <TipsAndUpdatesIcon sx={{ color: impactColors[item.impact], fontSize: 20 }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={600}>{item.title}</Typography>
                          <Chip label={item.impact} size="small" sx={{ bgcolor: `${impactColors[item.impact]}20`, color: impactColors[item.impact], fontWeight: 600, fontSize: "0.7rem" }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Stack>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Recommendations" subtitle="AI-generated actionable recommendations.">
              <Stack spacing={2}>
                {predictiveRecommendations.map((item, index) => (
                  <Card key={index} variant="outlined" sx={{ p: 2, borderLeft: `4px solid ${priorityColors[item.priority]}` }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                      <Avatar sx={{ bgcolor: `${priorityColors[item.priority]}20`, width: 40, height: 40 }}>
                        <RecommendIcon sx={{ color: priorityColors[item.priority], fontSize: 20 }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                          <Typography variant="subtitle2" fontWeight={600}>{item.title}</Typography>
                          <Chip label={item.priority} size="small" sx={{ bgcolor: `${priorityColors[item.priority]}20`, color: priorityColors[item.priority], fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase" }} />
                          <Chip icon={<ArrowUpwardIcon sx={{ fontSize: 14 }} />} label={`${item.metric}: ${item.projected_improvement}`} size="small" color="success" variant="outlined" />
                        </Box>
                        <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Stack>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Areas to Watch" subtitle="Predicted risks requiring attention.">
              <Stack spacing={2}>
                {predictiveAreasToWatch.map((item, index) => (
                  <Card key={index} variant="outlined" sx={{ p: 2, borderLeft: `4px solid ${severityColors[item.severity]}` }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                      <Avatar sx={{ bgcolor: `${severityColors[item.severity]}20`, width: 40, height: 40 }}>
                        <VisibilityIcon sx={{ color: severityColors[item.severity], fontSize: 20 }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                          <Typography variant="subtitle2" fontWeight={600}>{item.title}</Typography>
                          <Chip label={item.severity} size="small" sx={{ bgcolor: `${severityColors[item.severity]}20`, color: severityColors[item.severity], fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase" }} />
                          {item.affected_teams.map((t) => (
                            <Chip key={t} label={t} size="small" variant="outlined" />
                          ))}
                        </Box>
                        <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Stack>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
